// Copyright (c) Docugami, Inc. All rights reserved.

import { FontIcon, IOverflowSetItemProps, IconButton, OverflowSet, Spinner, SpinnerSize, TooltipDelay, TooltipHost } from "@fluentui/react";
import { tagFont, userFontFamily } from "components/common/Fonts";
import { DocumentBindingsConfirmationButton } from "components/common/ReportBindingConfirmationButtons";
import { ModalContentContainer } from "components/reports/ReportTableComponents";
import { appContentColor, primaryTheme } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { useAppSelector } from "hooks/redux";
import { useReportEntryActions } from "hooks/reportEntryActions";
import { useDgmlDownloadCommand, useDownloadDocumentCommand, useRemoveDocumentCommand, useRepublishDocumentCommand } from "hooks/WorkPathCommands";
import { useCallback, useMemo, useState } from "react";
import { selectReportId, selectReportLabels } from "store/reports/selectors";
import styled from "styled-components";
import { AutomationId, AutomationProps } from "utils/Automation";
import { RightOverlayedAddItemButton } from "./AddItemButton";
import { CellContentContainer, ControlsColumnContainer, ReportHeaderText, TitleCellContainer } from "./ReportGridCellStyles";
import { createFolderId } from "components/file_management/file_helpers";
import { useGetQualityControlWarningsQuery } from "store/frontend/frontendSlice";
import { useGetBindingsQuery, useGetEvaluationsPreviewQuery } from "store/reports/reportsSlice";
import { RowResizeableCell } from "./ReportGridRowResizableCell";

const Container = styled.div.attrs({
    'data-componentname': 'Container'
})`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-right: 1px solid;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background-color: rgb(150, 150, 200);
    z-index: 1;
  }
`;

const ControlsRow = styled.div.attrs({
    'data-componentname': 'ControlsRow'
})`
  display: flex;
  justify-content: right;
  align-items: center;
  padding: 5px 10px;
`;

const TitleContainer = styled.div.attrs({
    'data-componentname': 'TitleContainer'
})`
  flex-grow: 1;
  padding: 0 10px;
  overflow: hidden;
`;

const DocumentName = styled.div.attrs({
    'data-componentname': 'DocumentName'
}) <{ isClickable: boolean }>`
  font-family: ${userFontFamily};
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  color: ${primaryTheme.semanticColors.bodyText};
  word-break: break-word;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};

  &:hover {
    text-decoration: ${props => props.isClickable ? 'underline' : 'none'};
  }
`;

const SpinnerContainer = styled.div.attrs({
    'data-componentname': 'SpinnerContainer'
})`
  display: flex;
  align-items: center;
`;

const HeadingText = styled(ReportHeaderText).attrs({
    'data-componentname': 'EntryHeadingText'
})`
    font-family: ${tagFont};
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 1;
    margin: 0px 0px 0px 5px;
`;

interface DocumentTitleCellProps {
    icon?: string;
    folderId: string;
    fileName: string;
    statusMessage?: string;
    isDisabled?: boolean;
    onClick?: () => void;
    showDocumentConfirmation: boolean;
    canRepublish: boolean;
    isPublishing: boolean;
    entryIds: string[];
    onResize: (newHeight: number) => void;
}

export const DocumentTitleCell: React.FC<DocumentTitleCellProps> = ({
    icon,
    folderId,
    fileName,
    statusMessage,
    isDisabled,
    onClick,
    showDocumentConfirmation,
    canRepublish,
    isPublishing,
    entryIds,
    onResize,
}) => {
    const [mouseOver, setMouseOver] = useState(false);
    const reportId = useAppSelector(selectReportId);

    const [docsetId, fileId] = useMemo(() => {
        const fid = createFolderId(folderId);
        return [fid.clusterId, fid.fileId];
    }, [folderId]);

    useGetBindingsQuery({ reportId, fileId })
    useGetEvaluationsPreviewQuery({ reportId, fileId });
    useGetQualityControlWarningsQuery({ docsetId, fileId });

    const getDocumentTitleTooltip = () => (
        <>
            <p>{fileName}</p>
            {statusMessage && <p>{statusMessage}</p>}
        </>
    );

    const handleClick = useCallback(() => {
        if (!isDisabled && onClick) {
            onClick();
        }
    }, [isDisabled, onClick]);

    return (
        <RowResizeableCell onResize={onResize}>
            <Container
                data-automation-id={AutomationId.WorkPathGridDocumentTitleCell}
                onMouseEnter={() => setMouseOver(true)}
                onMouseLeave={() => setMouseOver(false)}>
                <ControlsRow>
                    {!isPublishing && showDocumentConfirmation && (
                        <DocumentBindingsConfirmationButton folderId={folderId} entryIds={entryIds} />
                    )}
                    {isPublishing && (
                        <SpinnerContainer>
                            <TooltipHost content="This document is publishing. When finished, it will show in the downloadable spreadsheet.">
                                <Spinner size={SpinnerSize.small} />
                            </TooltipHost>
                        </SpinnerContainer>
                    )}
                </ControlsRow>
                <TitleContainer>
                    <TooltipHost
                        styles={{ root: { display: 'flex', alignContent: 'center', height: '20px' } }}
                        delay={isDisabled ? undefined : TooltipDelay.long}
                        content={getDocumentTitleTooltip()}
                    >
                        <DocumentName
                            isClickable={!isDisabled && !!onClick}
                            onClick={handleClick}
                        >
                            {fileName}
                        </DocumentName>
                        <FlexGrowSpacer />
                        {icon && <FontIcon aria-label={icon} iconName={icon} style={{ marginLeft: '5px' }}/>}
                        { mouseOver && (
                            <DocumentOverflowMenuContainer>
                                <DocumentOverflowMenu
                                    folderId={folderId}
                                    documentNeedsRepublishing={canRepublish && !isDisabled}
                                />
                            </DocumentOverflowMenuContainer>
                        )}
                    </TooltipHost>

                </TitleContainer>
            </Container>
        </RowResizeableCell>
    );
};

interface DocumentOverflowMenuProps {
    folderId: string;
    documentNeedsRepublishing: boolean;
}

const DocumentOverflowMenu = (props: DocumentOverflowMenuProps): JSX.Element | null => {
    const republishDocument = useRepublishDocumentCommand(props.folderId, props.documentNeedsRepublishing);
    const downloadDgml = useDgmlDownloadCommand(props.folderId);
    const downloadFile = useDownloadDocumentCommand(props.folderId);
    const { command: removeDocument, overlay: removeDocumentOverlay } = useRemoveDocumentCommand(props.folderId);

    function onRenderOverflowButton(overflowItems: any[] | undefined): JSX.Element {
        return (
            <IconButton
                iconProps={{ iconName: "MoreVertical" }}
                styles={{
                    root: {
                        "i": {
                            fontWeight: 700
                        }
                    },
                    icon: {
                        fontWeight: 700,
                        color: appContentColor
                    },
                }}
                menuProps={{ items: overflowItems!, styles: { root: { backgroundColor: primaryTheme.palette.white } } }}
                onRenderMenuIcon={() => null} />
        )
    }

    const overflowItems = useMemo(() => {
        const commands = [republishDocument, downloadFile, downloadDgml, removeDocument];
        return commands.filter(c => !!c.isVisible);
    }, [downloadFile, downloadDgml, republishDocument, removeDocument]);

    return (
        <>
            <OverflowSet
                overflowItems={overflowItems}
                onRenderItem={() => null}
                onRenderOverflowButton={onRenderOverflowButton} />
            {removeDocumentOverlay}
        </>
    )
}

interface DocumentHeaderCellProps extends ReportTableOverflowMenuProps {
    reportId: string;
    includeAddButton: boolean;
}

export const DocumentHeaderCell = (props: DocumentHeaderCellProps): JSX.Element => {
    return (
        <CellContentContainer
            data-automation-id={props.automationId}
            showBottomBorder
        >
            <TitleCellContainer>
                <HeadingText>Document</HeadingText>

                <ControlsColumnContainer>
                    { !props.includeAddButton && <ReportTableOverflowMenu
                        automationId={AutomationId.WorkPathGridOverallMenu}
                        onRemoveAllEntries={props.onRemoveAllEntries}
                        canRemoveAllEntries={props.canRemoveAllEntries} /> }
                    { props.includeAddButton && <RightOverlayedAddItemButton /> }
                </ControlsColumnContainer>
            </TitleCellContainer>
        </CellContentContainer>
    );
}

interface ReportTableOverflowMenuProps extends AutomationProps {
    onRemoveAllEntries: () => void;
    canRemoveAllEntries: boolean;
}

const ReportTableOverflowMenu = (props: ReportTableOverflowMenuProps): JSX.Element | null => {
    const [removeAllModalHidden, setRemoveAllModalHidden] = useState(true);

    const reportLabels = useAppSelector(selectReportLabels);
    const { canRemoveEntry } = useReportEntryActions();

    function onRenderOverflowButton(overflowItems: IOverflowSetItemProps[] | undefined): JSX.Element {
        return (
            <IconButton
                iconProps={{ iconName: "MoreVertical" }}
                styles={{
                    root: {
                        "i": {
                            fontWeight: 700
                        }
                    },
                    icon: {
                        color: appContentColor
                    },
                }}
                menuProps={{ items: overflowItems!, styles: { root: { backgroundColor: primaryTheme.palette.white } } }}
                onRenderMenuIcon={() => null} />
        )
    }

    const overflowItems = useMemo(() => {
        const items: IOverflowSetItemProps[] = [];

        if (canRemoveEntry && !!props.onRemoveAllEntries) {
            items.push({
                key: 'removeAllEntries',
                name: 'Remove All Items',
                trigger: () => setRemoveAllModalHidden(false),
                onClick: () => setRemoveAllModalHidden(false),
                disabled: !props.canRemoveAllEntries
            });
        }

        return items;
    }, [canRemoveEntry, props.canRemoveAllEntries, props.onRemoveAllEntries]);

    return (
        <>
            <OverflowSet
                styles={{
                    root: {
                        display: 'inherit'
                    }
                }}
                data-automation-id={props.automationId}
                overflowItems={overflowItems}
                onRenderOverflowButton={onRenderOverflowButton}
                onRenderItem={() => null} />
            <ConfirmationDialog
                hidden={removeAllModalHidden}
                confirmButtonText="YES, REMOVE ALL"
                dismissButtonText='Cancel'
                confirmedCallback={() => {
                    props.onRemoveAllEntries();
                    setRemoveAllModalHidden(true);
                }}
                dismissedCallback={() => setRemoveAllModalHidden(true)}
                styles={{
                    main: {
                        selectors: {
                            '@media (min-width: 480px)': {
                                width: 600,
                                maxWidth: '1000px'
                            }
                        }
                    }
                }}>
                <ModalContentContainer>
                    This action will remove ALL items from your {reportLabels.itemsName} and leave it in a blank state. Proceed?
                </ModalContentContainer>
            </ConfirmationDialog>
        </>
    );
}

const DocumentOverflowMenuContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 20px;
    margin-right: -3px;
`;

const FlexGrowSpacer = styled.div`
    flex-grow: 1;
`;
